import { Component, OnInit, ViewChild } from '@angular/core';
import { AppInformationService, ArkUnitOfWork } from 'app/core/core.module';
import { Contribution, ContributionCash, ContributionLine } from 'app/core/model/entity-model';
import { DxDataGridComponent } from 'devextreme-angular';
import { confirm } from 'devextreme/ui/dialog';
import validationEngine from 'devextreme/ui/validation_engine';
import _ from 'lodash';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { CashListComponent } from '../cash-list/cash-list.component';

@Component({
    selector: 'app-bulk-contribution-dialog',
    templateUrl: './bulk-contribution-dialog.component.html',
    styleUrls: ['./bulk-contribution-dialog.component.scss'],
    standalone: false,
})
export class BulkContributionDialogComponent implements OnInit {
    result: Subject<any> = new Subject<any>();
    routerOption: any;
    contributionId: any;
    contributions: ContributionItem[] = [];
    contribution: ContributionItem;
    givingTypes: any[];
    people: any[];
    types: any[];
    otherAccounts: any[];
    specialAccountId: number;
    batchId: number;
    saveEnable = true;
    addProcessing = false;
    processing = false;
    checkNumberRequired = true;

    validationGroup = 'validationGroup';
    errorAction = 'adding';

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    @ViewChild('personSelect') personSelect: any; // QueryList<DxSelectBoxComponent>;

    constructor(
        public modalRef: BsModalRef,
        private uow: ArkUnitOfWork,
        private appInfo: AppInformationService,
        private modalService: BsModalService
    ) {}

    async ngOnInit() {
        this.contributions = []; // (await this.uow.getContributions(this.batchId)) as any[];
        this.people = await this.uow.getPeopleForGiving();
        this.types = await this.uow.getContributionTypes();
        this.otherAccounts = this.uow.otherAccounts();

        this.contribution = new ContributionItem();
        this.contribution.accountId = this.specialAccountId;
        this.contribution.givingTypeId = 1;
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift({
            location: 'before',
            template: 'toolbarButtons',
        });
    }

    validate() {
        const groupConfig = validationEngine.getGroupConfig(this.validationGroup);
        if (groupConfig) {
            const result = validationEngine.validateGroup(this.validationGroup);
            if (!result.isValid) {
                let message = '';
                result.brokenRules.forEach((brokenRule) => {
                    if (brokenRule.type === 'custom') message = `${brokenRule.message}\n${message}`;
                });
                this.appInfo.notifyToast(`Please fix errors before ${this.errorAction}.\n\n${message}`, 'error');
                return false;
            }
        }
        return true;
    }

    add() {
        const result = this.validate();
        if (!result) return;

        this.addProcessing = true;
        const previousGivingType = this.contribution.givingTypeId;
        this.contribution.id = this.contributions.length + 1;
        this.contribution.timeStamp = new Date(Date.now());

        this.contribution.givingName = (_(this.types).find({ id: this.contribution.givingTypeId } as any) as any).name;
        this.contribution.personName = (_(this.people).find({ id: this.contribution.personId } as any) as any).name;

        // console.log(this.contribution);
        this.contributions.push(this.contribution);

        this.contribution = new ContributionItem();
        this.contribution.accountId = this.specialAccountId;
        this.contribution.givingTypeId = previousGivingType;
        this.addProcessing = false;

        setTimeout(() => {
            // console.log(this.personSelect);
            this.personSelect.instance.focus();
        });
    }

    async addBatchCash() {
        let batchCash = await this.uow.getBatchCashByBatchId(this.batchId) as ContributionCash;
        if (!batchCash) batchCash = this.uow.contributionCashes.createEntity() as ContributionCash;

        const modalRef = this.modalService.show(CashListComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                mode: 'batchCash',
                batchId: this.batchId,
                contributionCash: batchCash,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result) {
            }
        });
    }

    remove() {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0] as any;
        if (!selectedRow) return;

        this.contributions.splice(_.findIndex(this.contributions, { id: selectedRow.id }), 1);
        this.updateDisplayOrder();
    }

    updateDisplayOrder() {
        _.orderBy(this.contributions, 'id').forEach((line, index) => {
            if (line.id !== index + 1) line.id = index + 1;
        });
    }

    async save() {
        this.processing = true;
        if (this.contributions) {
            this.contributions.forEach((contribution) => {
                const donation = this.uow.manager.createEntity('Contribution', {
                    batchId: this.batchId,
                    typeId: contribution.givingTypeId,
                    personId: contribution.personId,
                    note: contribution.note,
                    created: new Date(Date.now()),
                    checkNumber: contribution.checkNumber,
                }) as Contribution;
                const donationLine = this.uow.manager.createEntity('ContributionLine', {
                    contributionId: donation.id,
                    accountId: contribution.accountId,
                    amount: contribution.amount,
                    created: new Date(Date.now()),
                }) as ContributionLine;
            });
        }
        await this.uow.commit();
        this.result.next({ result: true });
        this.modalRef.hide();
    }

    async cancel() {
        if (this.contributions.length !== 0) {
            const dialogResult = await (<Promise<boolean>>confirm('Are you sure you want to cancel?', 'Confirm Cancel'));
            if (!dialogResult) return;
        }
        this.uow.rollback();
        this.modalRef.hide();
    }

    givingTypeChanged = (e) => {
        this.checkNumberRequired = this.contribution.givingTypeId === 1;
    };

    async envelopeChanged(e) {
        if (!this.contribution.envelope) return;
        let persons = _(this.people).find(((user) => {
            return user.envelope === this.contribution.envelope && user.sex === 'M';
        }) as any) as any;
        if (!!persons) this.contribution.personId = persons.id;
        else {
            persons = _(this.people).find(((user) => {
                return user.envelope === this.contribution.envelope;
            }) as any) as any;
            if (!!persons) this.contribution.personId = persons.id;
            else this.contribution.personId = 3794;
        }
    }
}
class ContributionItem {
    id: number;
    accountId: number;
    givingTypeId: number;
    givingName: string;
    personName: string;
    personId: number;
    amount: number;
    timeStamp: Date;
    envelope: any;
    note: any;
    checkNumber: string;

    constructor() {}
}
